import React, { useState, useEffect, useRef } from 'react';
import './Freundebuch.css';
import { checkField } from '../../services/pkiApi';

const TOTAL_PAGES = 12;

const MOODS = [
  { value: 'happy', label: 'Happy', icon: '😊' },
  { value: 'okay', label: 'Ganz okay', icon: '😐' },
  { value: 'sad', label: 'Traurig', icon: '😢' },
  { value: 'angry', label: 'Wütend', icon: '😠' },
];

const AGES = [
  { value: '14-20', label: '14–20', icon: '🧒' },
  { value: '20-35', label: '20–35', icon: '🧑' },
  { value: '35-60', label: '35–60', icon: '🧔' },
  { value: '60+', label: '60+', icon: '👴' },
];

const LIVING = [
  { value: 'city', label: 'Stadt', icon: '🏙️' },
  { value: 'country', label: 'Land', icon: '🌳' },
];

const COLORS = [
  { value: 'rot', color: '#ff6b6b' },
  { value: 'gelb', color: '#feca57' },
  { value: 'grün', color: '#1dd1a1' },
  { value: 'blau', color: '#48dbfb' },
  { value: 'orange', color: '#ff9f43' },
  { value: 'pink', color: '#ff9ff3' },
  { value: 'lila', color: '#a55eea' },
  { value: 'türkis', color: '#00d2d3' },
  { value: 'weiß', color: '#ffffff' },
];

const ACTIVITIES = [
  { value: 'working', label: 'Arbeiten', icon: '💼' },
  { value: 'chilling', label: 'Chillen', icon: '🛋️' },
  { value: 'cooking', label: 'Kochen', icon: '🍳' },
  { value: 'sports', label: 'Sport', icon: '🏃' },
  { value: 'eating', label: 'Essen', icon: '🍕' },
  { value: 'tv', label: 'Fernsehen', icon: '📺' },
  { value: 'learning', label: 'Lernen', icon: '📚' },
  { value: 'custom', label: 'Individuell', icon: '✏️' },
];

const INTERESTS = [
  { value: 'tech', label: 'Tech', icon: '💻' },
  { value: 'music', label: 'Musik', icon: '🎵' },
  { value: 'sports', label: 'Sport', icon: '⚡' },
  { value: 'art', label: 'Kunst', icon: '🎨' },
  { value: 'food', label: 'Food', icon: '🍜' },
  { value: 'travel', label: 'Reisen', icon: '✈️' },
  { value: 'gaming', label: 'Gaming', icon: '🎮' },
  { value: 'film', label: 'Film', icon: '🎬' },
  { value: 'nature', label: 'Natur', icon: '🌿' },
  { value: 'fitness', label: 'Fitness', icon: '💪' },
  { value: 'fashion', label: 'Mode', icon: '👗' },
  { value: 'science', label: 'Wissenschaft', icon: '🔬' },
  { value: 'cars', label: 'Autos', icon: '🚗' },
  { value: 'animals', label: 'Tiere', icon: '🐾' },
  { value: 'comedy', label: 'Comedy', icon: '😂' },
];

const GENRES = [
  { value: 'pop', label: 'Pop' },
  { value: 'edm', label: 'EDM' },
  { value: 'hiphop', label: 'Hip-Hop' },
  { value: 'rock', label: 'Rock' },
  { value: 'indie', label: 'Indie' },
  { value: 'rnb', label: 'R&B' },
  { value: 'dance', label: 'Dance' },
  { value: 'reggae', label: 'Reggae' },
  { value: 'deutschpop', label: 'Deutschpop' },
  { value: 'latin', label: 'Latin' },
  { value: 'country', label: 'Country' },
];

const EDM_SUBGENRES = [
  { value: 'progressive-house', label: 'Progressive House' },
  { value: 'deep-house', label: 'Deep House' },
  { value: 'electro-house', label: 'Electro House' },
  { value: 'tropical-house', label: 'Tropical House' },
  { value: 'bigroom-house', label: 'Big Room House' },
  { value: 'future-house', label: 'Future House' },
  { value: 'tech-house', label: 'Tech House' },
  { value: 'eurodance', label: 'Eurodance' },
  { value: 'future-bass', label: 'Future Bass' },
  { value: 'trance', label: 'Trance' },
];

const ACTIVITY_MAP = {
  working: 'Arbeiten',
  chilling: 'Chillen',
  cooking: 'Kochen',
  sports: 'Sport',
  eating: 'Essen',
  tv: 'Fernsehen',
  learning: 'Lernen',
  custom: 'Individuell',
};

const MOOD_MAP = {
  angry: 'Wütend',
  sad: 'Traurig',
  okay: 'Ganz okay',
  happy: 'Happy',
};

const MOOD_EMOJI = {
  angry: '😠',
  sad: '😢',
  okay: '😐',
  happy: '😊',
};

const COLOR_MAP = {
  rot: '#ff6b6b',
  gelb: '#feca57',
  grün: '#1dd1a1',
  blau: '#48dbfb',
  orange: '#ff9f43',
  pink: '#ff9ff3',
  lila: '#a55eea',
  türkis: '#00d2d3',
  weiß: '#ffffff',
};

const LIVING_MAP = {
  city: 'Stadt',
  country: 'Land',
};

export function Freundebuch({ onComplete, onQuestionChange, onNavigationChange, onJoinClick }) {
  const [currentPage, setCurrentPage] = useState(0);
  const [welcomeShown, setWelcomeShown] = useState(false);
  const [welcomeActive, setWelcomeActive] = useState(false);
  const [welcomeFadeOut, setWelcomeFadeOut] = useState(false);
  const [welcomeTextPhase, setWelcomeTextPhase] = useState(0);
  const [namePageFadeOut, setNamePageFadeOut] = useState(false);
  const [dragState, setDragState] = useState({ dragging: null, over: null });
  const [customDrafts, setCustomDrafts] = useState([]); // offene Eingabe-Pillen (IDs)
  const [draftHasText, setDraftHasText] = useState(false); // hat der aktuelle Draft mind. 1 Zeichen

  const [userData, setUserData] = useState({
    name: '',
    mood: '',
    age: '',
    city: '',
    living: '',
    job: '',
    activity: '',
    customActivity: '',
    interests: [],
    customInterests: [],
    genres: [],
    edmSubgenres: [],
    color: '',
    dayHighlights: [], // Array von { id, time, text }
  });

  const nameInputRef = useRef(null);
  const startTimeRef = useRef(null);

  // Start timer when user begins (after welcome)
  useEffect(() => {
    if (!welcomeActive && currentPage === 0 && !startTimeRef.current) {
      startTimeRef.current = Date.now();
    }
  }, [welcomeActive, currentPage]);

  // Update parent with current question
  useEffect(() => {
    if (onQuestionChange) {
      // Hide question during welcome animation
      if (welcomeActive) {
        onQuestionChange('');
        return;
      }
      const questions = [
        'Wie heißt du?',
        'Trage dich ins Freundebuch ein',
        'Trage dich ins Freundebuch ein',
        'Trage dich ins Freundebuch ein',
        'Trage dich ins Freundebuch ein',
        'Trage dich ins Freundebuch ein',
        'Trage dich ins Freundebuch ein',
        'Trage dich ins Freundebuch ein',
        'Das bist du',
        '',
        '',
        'Welche Musik hörst du?',
      ];
      onQuestionChange(questions[currentPage] || '');
    }
  }, [currentPage, welcomeActive, onQuestionChange]);

  // Focus name input on mount
  useEffect(() => {
    if (nameInputRef.current && currentPage === 0) {
      nameInputRef.current.focus();
    }
  }, [currentPage]);

  const updateUserData = (key, value) => {
    setUserData(prev => ({ ...prev, [key]: value }));
  };

  // Reset form and go back to name page
  const resetForm = () => {
    setUserData({
      name: '',
      mood: '',
      age: '',
      city: '',
      living: '',
      job: '',
      activity: '',
      customActivity: '',
      interests: [],
      customInterests: [],
      genres: [],
      edmSubgenres: [],
      color: '',
      dayHighlights: [],
    });
    setCustomDrafts([]);
    setDraftHasText(false);
    setWelcomeShown(false);
    setCurrentPage(0);
    startTimeRef.current = null; // Timer zurücksetzen
  };

  const totalInterests = () => userData.interests.length + userData.customInterests.length + customDrafts.length;

  const toggleInterest = (value) => {
    setUserData(prev => {
      const isSelected = prev.interests.includes(value);
      if (!isSelected && (prev.interests.length + prev.customInterests.length + customDrafts.length) >= 5) {
        return prev;
      }
      return {
        ...prev,
        interests: isSelected
          ? prev.interests.filter(i => i !== value)
          : [...prev.interests, value],
      };
    });
  };

  const openCustomDraft = () => {
    if (totalInterests() >= 5) return;
    if (customDrafts.length > 0) return; // max 1 Draft
    setDraftHasText(false);
    setCustomDrafts([Date.now()]);
  };

  const submitCustomDraft = (draftId, text) => {
    const trimmed = text.trim();
    setCustomDrafts(prev => prev.filter(id => id !== draftId));
    setDraftHasText(false);
    if (!trimmed) return;
    setUserData(prev => {
      if ((prev.interests.length + prev.customInterests.length) >= 5) return prev;
      if (prev.customInterests.includes(trimmed)) return prev;
      return { ...prev, customInterests: [...prev.customInterests, trimmed] };
    });
  };

  const removeCustomInterest = (index) => {
    setUserData(prev => ({
      ...prev,
      customInterests: prev.customInterests.filter((_, i) => i !== index),
    }));
  };

  const toggleGenre = (value) => {
    setUserData(prev => {
      const isSelected = prev.genres.includes(value);
      const newGenres = isSelected
        ? prev.genres.filter(g => g !== value)
        : [...prev.genres, value];
      // Select all sub-genres when EDM is selected, clear when deselected
      const newEdmSubgenres = value === 'edm'
        ? (isSelected ? [] : EDM_SUBGENRES.map(s => s.value))
        : prev.edmSubgenres;
      return {
        ...prev,
        genres: newGenres,
        edmSubgenres: newEdmSubgenres,
      };
    });
  };

  const toggleEdmSubgenre = (value) => {
    setUserData(prev => {
      const isSelected = prev.edmSubgenres.includes(value);
      return {
        ...prev,
        edmSubgenres: isSelected
          ? prev.edmSubgenres.filter(s => s !== value)
          : [...prev.edmSubgenres, value],
      };
    });
  };

  // Day Highlights Funktionen
  const addHighlight = () => {
    const newHighlight = {
      id: Date.now(),
      time: '',
      text: '',
    };
    setUserData(prev => ({
      ...prev,
      dayHighlights: [...prev.dayHighlights, newHighlight],
    }));
  };

  const updateHighlight = (id, field, value) => {
    setUserData(prev => ({
      ...prev,
      dayHighlights: prev.dayHighlights.map(h =>
        h.id === id ? { ...h, [field]: value } : h
      ),
    }));
  };

  const removeHighlight = (id) => {
    setUserData(prev => ({
      ...prev,
      dayHighlights: prev.dayHighlights.filter(h => h.id !== id),
    }));
  };

  const moveHighlight = (fromIndex, toIndex) => {
    setUserData(prev => {
      const highlights = [...prev.dayHighlights];
      const [moved] = highlights.splice(fromIndex, 1);
      highlights.splice(toIndex, 0, moved);
      return { ...prev, dayHighlights: highlights };
    });
  };

  // Berechnet die visuelle Reihenfolge während des Draggings
  const getVisualHighlights = () => {
    const { dragging, over } = dragState;
    if (dragging === null || over === null || dragging === over) {
      return userData.dayHighlights;
    }
    const result = [...userData.dayHighlights];
    const [moved] = result.splice(dragging, 1);
    result.splice(over, 0, moved);
    return result;
  };

  const showWelcome = () => {
    // 1. Name-Page ausblenden
    setNamePageFadeOut(true);

    setTimeout(() => {
      // 2. Welcome-Overlay aktivieren - zeigt "Hi Name"
      setWelcomeActive(true);
      setWelcomeTextPhase(0); // Nur Greeting sichtbar

      // 3. "Hi Name" ausblenden, Text 1 einblenden
      setTimeout(() => setWelcomeTextPhase(1), 1500);

      // 4. Text 1 ausblenden, Text 2 einblenden
      setTimeout(() => setWelcomeTextPhase(2), 4000);

      // 5. Alles ausblenden und weiter
      setTimeout(() => {
        setWelcomeFadeOut(true);
        setTimeout(() => {
          setWelcomeActive(false);
          setWelcomeFadeOut(false);
          setWelcomeShown(true);
          setNamePageFadeOut(false);
          setCurrentPage(1);
        }, 400);
      }, 6500);
    }, 400);
  };

  // Mapping: Welche Seite hat welches Textfeld?
  const PAGE_TEXT_FIELDS = {
    0: 'name',
    3: 'city',
    4: 'customActivity',
    5: 'job',
  };

  const checkCurrentPageField = async () => {
    // Seite 6: Eigene Interessen prüfen
    if (currentPage === 6) {
      const toRemove = [];
      for (let i = 0; i < userData.customInterests.length; i++) {
        const text = userData.customInterests[i];
        if (!text || !text.trim()) continue;
        try {
          const result = await checkField('customInterest', text);
          if (result.replaced) {
            toRemove.push(i);
          }
        } catch (error) {
          // Silent error handling
        }
      }
      if (toRemove.length > 0) {
        setUserData(prev => ({
          ...prev,
          customInterests: prev.customInterests.filter((_, i) => !toRemove.includes(i)),
        }));
      }
      return true;
    }

    // Seite 7: Tagesplan - alle Highlight-Texte prüfen
    if (currentPage === 7) {
      const highlightsToRemove = [];
      for (const highlight of userData.dayHighlights) {
        if (!highlight.text || !highlight.text.trim()) continue;
        try {
          const result = await checkField('dayHighlight', highlight.text);
          if (result.replaced) {
            // Gesamten Eintrag entfernen statt Text ersetzen
            highlightsToRemove.push(highlight.id);
          }
        } catch (error) {
          // Silent error handling
        }
      }
      // Alle geflaggten Einträge entfernen
      highlightsToRemove.forEach(id => removeHighlight(id));
      return true;
    }

    const fieldName = PAGE_TEXT_FIELDS[currentPage];
    if (!fieldName) return true; // Keine Textfelder auf dieser Seite

    // customActivity nur prüfen wenn "Individuell" ausgewählt
    if (fieldName === 'customActivity' && userData.activity !== 'custom') {
      return true;
    }

    const value = userData[fieldName];
    if (!value || !value.trim()) return true; // Leere Werte überspringen

    try {
      const result = await checkField(fieldName, value);
      if (result.replaced) {
        updateUserData(fieldName, result.value);
      }
      return true;
    } catch (error) {
      return true; // Bei Fehler trotzdem weitermachen
    }
  };

  const nextPage = () => {
    // Textfeld im Hintergrund prüfen (blockiert Navigation nicht)
    checkCurrentPageField();

    if (currentPage === 0 && !welcomeShown) {
      showWelcome();
      return;
    }
    if (currentPage < TOTAL_PAGES - 1) {
      setCurrentPage(currentPage + 1);
    } else if (currentPage === TOTAL_PAGES - 1) {
      // Fertig - an Parent übergeben
      if (onComplete) {
        // Antwortzeit in Sekunden berechnen
        const elapsedSeconds = startTimeRef.current
          ? Math.round((Date.now() - startTimeRef.current) / 1000)
          : 60; // Default 60s falls Timer nicht gestartet
        // Filter out empty dayHighlights (no text content)
        const filteredHighlights = userData.dayHighlights.filter(h => h.text && h.text.trim() !== '');
        onComplete({ ...userData, dayHighlights: filteredHighlights, responseTimeSeconds: elapsedSeconds });
      }
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToPage = (index) => {
    if (welcomeShown && index === 0) return;
    if (index <= currentPage + 1) {
      setCurrentPage(index);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
      nextPage();
    }
  };

  // Location display helper
  const getLocationDisplay = () => {
    const parts = [userData.city, LIVING_MAP[userData.living]].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  const isNamePage = currentPage === 0;

  // Check if current page has required selection
  const canProceed = () => {
    switch (currentPage) {
      case 0: return userData.name.trim() !== '';
      case 1: return userData.mood !== '';
      case 2: return userData.age !== '';
      case 3: return userData.living !== ''; // Stadt/Land erforderlich
      case 4:
        // For custom activity, require text input
        if (userData.activity === 'custom') {
          return userData.customActivity.trim() !== '';
        }
        return userData.activity !== '';
      case 5: return userData.color !== '' && userData.job.trim() !== ''; // Farbe & Beruf erforderlich
      case 6:
        return (userData.interests.length + userData.customInterests.length) >= 1;
      case 7: return true; // Tagesplan optional
      case 8: return true; // Zusammenfassung
      case 9: return true; // Übergangsseite 1
      case 10: return true; // Übergangsseite 2
      case 11: return userData.genres.length >= 1; // Min 1 Genre
      default: return true;
    }
  };

  const isTransitionPage = currentPage === 9 || currentPage === 10;
  const isMusicPage = currentPage === 11;
  const showBackButton = !isNamePage && currentPage >= 1 && !welcomeActive && !isTransitionPage && !isMusicPage;
  const isResetButton = currentPage === 1; // Page 1 shows X button instead of back arrow
  const showNextButton = !welcomeActive && !isTransitionPage;
  const nextButtonDisabled = !canProceed();
  const isLastPage = currentPage === TOTAL_PAGES - 1; // Seite 11 = letzte Seite

  // Auto-advance from transition pages (Seite 9 und 10)
  useEffect(() => {
    if (currentPage === 9) {
      const timer = setTimeout(() => {
        setCurrentPage(10);
      }, 2000);
      return () => clearTimeout(timer);
    }
    if (currentPage === 10) {
      const timer = setTimeout(() => {
        setCurrentPage(11);
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [currentPage]);

  // Notify parent about navigation state
  useEffect(() => {
    if (onNavigationChange) {
      onNavigationChange({
        showBack: showBackButton,
        showNext: showNextButton,
        nextDisabled: nextButtonDisabled,
        isLastPage,
        isResetButton,
        isNamePage,
        currentPage,
        onBack: isResetButton ? resetForm : prevPage,
        onNext: nextPage,
      });
    }
  }, [currentPage, welcomeActive, userData, onNavigationChange, showBackButton, showNextButton, nextButtonDisabled, isLastPage, isResetButton]);

  return (
    <div className="freundebuch" onKeyDown={handleKeyDown} tabIndex={0}>

      {/* Progress Bar - unten mittig (Seiten 1-8, ohne Name, Übergang, Musik) */}
      <div className={`fb-progress ${isNamePage || welcomeActive || currentPage >= 9 ? 'hidden' : ''}`}>
        {Array.from({ length: 8 }).map((_, i) => {
          const pageIndex = i + 1; // Offset um 1, da Page 0 (Name) nicht zählt
          return (
            <div
              key={i}
              className={`fb-progress-segment ${pageIndex === currentPage ? 'active' : ''} ${pageIndex < currentPage ? 'completed' : ''}`}
              onClick={() => goToPage(pageIndex)}
            />
          );
        })}
      </div>

      {/* Main Layout */}
      <div className="fb-layout">
        {/* Content */}
        <div className="fb-content">
          {/* Welcome Overlay */}
          <div className={`fb-welcome-overlay ${welcomeActive ? 'active' : ''} ${welcomeFadeOut ? 'fade-out' : ''}`}>
            <h1
              className="fb-welcome-greeting"
              style={{
                animation: welcomeTextPhase === 0 ? 'fbSlideUp 0.5s ease forwards' :
                          welcomeTextPhase >= 1 ? 'fbFadeOut 0.4s ease forwards' : 'none',
              }}
            >
              Hi <span className="fb-welcome-name">{userData.name || 'du'}</span>!
            </h1>
            <div className="fb-welcome-text-container">
              <p
                className="fb-welcome-text"
                style={{
                  animation: welcomeTextPhase === 1 ? 'fbSlideUpCenter 0.5s ease forwards' :
                            welcomeTextPhase >= 2 ? 'fbFadeOutCenter 0.4s ease forwards' : 'none',
                }}
              >
                Willkommen bei <span className="fb-welcome-brand">schmalgsicht v2</span> –<br />
                dem Radio für <span className="fb-welcome-brand">DEIN</span> Gesicht.
              </p>
              <p
                className="fb-welcome-text"
                style={{
                  animation: welcomeTextPhase === 2 ? 'fbSlideUpCenter 0.5s ease forwards' : 'none',
                }}
              >
                Damit wir <span className="fb-welcome-brand">DICH</span> etwas besser kennenlernen können,<br />
                trag dich in unser <span className="fb-welcome-brand">Freundebuch</span> ein.
              </p>
            </div>
          </div>

          {/* Page 0: Name */}
          <div className={`fb-page fb-page-name ${currentPage === 0 && !welcomeActive ? 'active' : ''} ${namePageFadeOut ? 'fade-out' : ''}`}>
            <div className="fb-name-page">
              <h1 className="fb-name-title">Wie heißt du?</h1>
              <input
                ref={nameInputRef}
                type="text"
                className="fb-text-input fb-name-input"
                value={userData.name}
                onChange={(e) => updateUserData('name', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && nextPage()}
              />
              {onJoinClick && (
                <div className="fb-join-link">
                  oder <button onClick={onJoinClick} className="fb-join-link-text">einem Stream beitreten</button>
                </div>
              )}
            </div>
          </div>

      {/* Page 1: Mood */}
      <div className={`fb-page ${currentPage === 1 ? 'active' : ''}`}>
        <div className="fb-page-content">
          <h1 className="fb-question-title">Wie geht's dir gerade?</h1>
          <div className="fb-card-grid four-col">
            {MOODS.map((mood) => (
              <div
                key={mood.value}
                className={`fb-card compact ${userData.mood === mood.value ? 'selected' : ''}`}
                onClick={() => updateUserData('mood', mood.value)}
              >
                <span className="fb-card-icon">{mood.icon}</span>
                <span className="fb-card-label">{mood.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Page 2: Alter */}
      <div className={`fb-page ${currentPage === 2 ? 'active' : ''}`}>
        <div className="fb-page-content">
          <h1 className="fb-question-title">Wie alt bist du?</h1>

          <div className="fb-card-grid four-col">
            {AGES.map((age) => (
              <div
                key={age.value}
                className={`fb-card compact ${userData.age === age.value ? 'selected' : ''}`}
                onClick={() => updateUserData('age', age.value)}
              >
                <span className="fb-card-icon">{age.icon}</span>
                <span className="fb-card-label">{age.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Page 3: Ort */}
      <div className={`fb-page ${currentPage === 3 ? 'active' : ''}`}>
        <div className="fb-page-content">
          <h1 className="fb-question-title">Wo bist du zuhause?</h1>

          <div className="fb-input-group">
            <input
              type="text"
              className="fb-text-input"
              placeholder="Stadt oder Region (optional)"
              value={userData.city}
              onChange={(e) => updateUserData('city', e.target.value)}
            />
          </div>

          <div className="fb-input-group">
            <label className="fb-input-label">Lebst du eher in der...</label>
            <div className="fb-card-grid two-col">
              {LIVING.map((living) => (
                <div
                  key={living.value}
                  className={`fb-card compact ${userData.living === living.value ? 'selected' : ''}`}
                  onClick={() => updateUserData('living', living.value)}
                >
                  <span className="fb-card-icon">{living.icon}</span>
                  <span className="fb-card-label">{living.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Page 4: Was machst du gerade */}
      <div className={`fb-page ${currentPage === 4 ? 'active' : ''}`}>
        <div className="fb-page-content">
          <h1 className="fb-question-title">Was machst du gerade?</h1>

          <div className="fb-card-grid four-col">
            {ACTIVITIES.map((activity) => (
              activity.value === 'custom' ? (
                <div
                  key={activity.value}
                  className={`fb-card compact fb-card-custom ${userData.activity === 'custom' ? 'selected' : ''}`}
                  onClick={() => updateUserData('activity', 'custom')}
                >
                  <span className="fb-card-icon">+</span>
                  {userData.activity === 'custom' ? (
                    <input
                      type="text"
                      className="fb-card-label fb-custom-activity-inline"
                      autoFocus
                      value={userData.customActivity}
                      placeholder="z.B. Tanzen"
                      onChange={(e) => updateUserData('customActivity', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <span className="fb-card-label">{userData.customActivity || 'Eigene'}</span>
                  )}
                </div>
              ) : (
                <div
                  key={activity.value}
                  className={`fb-card compact ${userData.activity === activity.value ? 'selected' : ''}`}
                  onClick={() => updateUserData('activity', activity.value)}
                >
                  <span className="fb-card-icon">{activity.icon}</span>
                  <span className="fb-card-label">{activity.label}</span>
                </div>
              )
            ))}
          </div>
        </div>
      </div>

      {/* Page 5: Beruf & Farbe */}
      <div className={`fb-page ${currentPage === 5 ? 'active' : ''}`}>
        <div className="fb-page-content">
          <h1 className="fb-question-title">Noch ein paar Details</h1>

          <div className="fb-input-group">
            <label className="fb-input-label">Was machst du beruflich?</label>
            <input
              type="text"
              className="fb-text-input"
              placeholder="z.B. Student, Designer, ..."
              value={userData.job}
              onChange={(e) => updateUserData('job', e.target.value)}
            />
          </div>

          <div className="fb-input-group">
            <label className="fb-input-label">Deine Lieblingsfarbe</label>
            <div className="fb-color-grid">
              {COLORS.map((color) => (
                <div
                  key={color.value}
                  className={`fb-color-option ${userData.color === color.value ? 'selected' : ''}`}
                  style={{ backgroundColor: color.color }}
                  onClick={() => updateUserData('color', color.value)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Page 6: Interessen */}
      <div className={`fb-page fb-page-wide ${currentPage === 6 ? 'active' : ''}`}>
        <div className="fb-page-content fb-page-content-wide">
          <h1 className="fb-question-title">Wofür interessierst du dich?</h1>
          <label className="fb-input-label">
            {(() => {
              const total = userData.interests.length + userData.customInterests.length;
              return `Wähle bis zu 5 Interessen aus (${total}/5)`;
            })()}
          </label>

          <div className="fb-input-group">
            <div className={`fb-chip-container ${totalInterests() >= 5 ? 'fb-chips-maxed' : ''}`}>
              {INTERESTS.map((interest) => (
                <div
                  key={interest.value}
                  className={`fb-chip ${userData.interests.includes(interest.value) ? 'selected' : ''}`}
                  onClick={() => toggleInterest(interest.value)}
                >
                  <span className="fb-chip-icon">{interest.icon}</span>
                  {interest.label}
                </div>
              ))}
            </div>
          </div>

          <div className="fb-custom-interests-section">
            <div className="fb-custom-interests-divider" />
            <label className="fb-input-label">Oder füge eigene Interessen hinzu</label>
            <div className="fb-chip-container">
              {userData.customInterests.map((ci, idx) => (
                <div
                  key={`custom-${idx}`}
                  className="fb-chip selected"
                  onClick={() => removeCustomInterest(idx)}
                >
                  {ci}
                </div>
              ))}
              {customDrafts.length === 0 && (
                <div
                  className={`fb-chip fb-chip-add ${totalInterests() >= 5 ? 'fb-chip-disabled' : ''}`}
                  onClick={openCustomDraft}
                >
                  + Eigene
                </div>
              )}
              {customDrafts.length > 0 && (
                <div className="fb-chip fb-chip-add selected">
                  <input
                    type="text"
                    className="fb-chip-add-input"
                    autoFocus
                    placeholder="z.B. Kochen"
                    maxLength={30}
                    onChange={(e) => setDraftHasText(e.target.value.length > 0)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        submitCustomDraft(customDrafts[0], e.target.value);
                      }
                    }}
                    onBlur={(e) => {
                      submitCustomDraft(customDrafts[0], e.target.value);
                    }}
                  />
                </div>
              )}
              {customDrafts.length > 0 && draftHasText && totalInterests() < 5 && (
                <div className="fb-chip fb-chip-add" onClick={openCustomDraft}>
                  + Eigene
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Page 7: Beschreibe deinen Tag */}
      <div className={`fb-page ${currentPage === 7 ? 'active' : ''}`}>
        <div className="fb-page-content fb-page-content-wide">
          <h1 className="fb-question-title">Beschreibe deinen Tag</h1>
          <label className="fb-input-label">Dieser Schritt ist optional</label>

          <div className="fb-day-planner">
            <div className="fb-highlights-list">
              {getVisualHighlights().map((highlight, visualIndex) => {
                const realIndex = userData.dayHighlights.findIndex(h => h.id === highlight.id);
                const isDragging = dragState.dragging === realIndex;
                return (
                <div
                  key={highlight.id}
                  className={`fb-highlight-card ${isDragging ? 'dragging' : ''}`}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    setDragState({ dragging: realIndex, over: realIndex });
                  }}
                  onDragEnd={() => {
                    if (dragState.dragging !== null && dragState.over !== null) {
                      moveHighlight(dragState.dragging, dragState.over);
                    }
                    setDragState({ dragging: null, over: null });
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (dragState.over !== realIndex) {
                      setDragState(prev => ({ ...prev, over: realIndex }));
                    }
                  }}
                >
                  <div className="fb-highlight-drag-handle">⋮⋮</div>
                  <input
                    type="time"
                    className="fb-highlight-time"
                    value={highlight.time}
                    onChange={(e) => updateHighlight(highlight.id, 'time', e.target.value)}
                  />
                  <input
                    type="text"
                    className="fb-highlight-text"
                    placeholder="Was machst du?"
                    value={highlight.text}
                    onChange={(e) => updateHighlight(highlight.id, 'text', e.target.value)}
                  />
                  <button
                    className="fb-highlight-remove"
                    onClick={() => removeHighlight(highlight.id)}
                  >
                    ×
                  </button>
                </div>
              );
              })}
            </div>

            {userData.dayHighlights.length < 4 && (
              <button className="fb-add-highlight" onClick={addHighlight}>
                + Highlight hinzufügen
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Page 8: Zusammenfassung */}
      <div className={`fb-page ${currentPage === 8 ? 'active' : ''}`}>
        <div className="fb-page-content fb-page-content-wide" style={{ transform: 'translateY(-20px)' }}>
          <h1 className="fb-question-title">Das bist <span className="fb-gradient-text">DU</span></h1>

          <div className="fb-bento">
            {/* Name & Avatar - große Zelle */}
            <div className="fb-bento-cell fb-bento-profile">
              <div
                className="fb-bento-avatar"
                style={userData.color ? { background: COLOR_MAP[userData.color] } : {}}
              >
                {MOOD_EMOJI[userData.mood] || '👤'}
              </div>
              <div className="fb-bento-name">{userData.name || 'Anonym'}</div>
            </div>

            {/* Stimmung */}
            <div className="fb-bento-cell">
              <span className="fb-bento-label">Stimmung</span>
              <span className="fb-bento-value">{MOOD_MAP[userData.mood] || '—'}</span>
            </div>

            {/* Alter */}
            <div className="fb-bento-cell">
              <span className="fb-bento-label">Alter</span>
              <span className="fb-bento-value">{userData.age || '—'}</span>
            </div>

            {/* Ort */}
            <div className="fb-bento-cell fb-bento-span2 fb-bento-location">
              <span className="fb-bento-label">Ort</span>
              <span className="fb-bento-value">{getLocationDisplay() || '—'}</span>
            </div>

            {/* Aktivität */}
            <div className="fb-bento-cell fb-bento-activity">
              <span className="fb-bento-label">Gerade</span>
              <span className="fb-bento-value">
                {userData.activity === 'custom'
                  ? (userData.customActivity || 'Individuell')
                  : (ACTIVITY_MAP[userData.activity] || '—')}
              </span>
            </div>

            {/* Beruf */}
            <div className="fb-bento-cell fb-bento-span2 fb-bento-job">
              <span className="fb-bento-label">Beruf</span>
              <span className="fb-bento-value">{userData.job}</span>
            </div>

            {/* Farbe */}
            <div className="fb-bento-cell fb-bento-color-cell">
              <span className="fb-bento-label">Farbe</span>
              <div className="fb-bento-color-value">
                <span
                  className="fb-bento-color-dot"
                  style={{ backgroundColor: COLOR_MAP[userData.color] || '#ccc' }}
                />
                <span className="fb-bento-value">{userData.color ? userData.color.charAt(0).toUpperCase() + userData.color.slice(1) : '—'}</span>
              </div>
            </div>

            {/* Interessen - breite Zelle */}
            <div className="fb-bento-cell fb-bento-wide">
              <span className="fb-bento-label">Interessen</span>
              <div className="fb-bento-tags">
                {(userData.interests.length + userData.customInterests.length) > 0 ? (
                  <>
                    {userData.interests.map((interest) => {
                      const match = INTERESTS.find(i => i.value === interest);
                      return (
                        <span key={interest} className="fb-bento-tag">
                          {match ? match.label : interest}
                        </span>
                      );
                    })}
                    {userData.customInterests.map((ci, idx) => (
                      <span key={`custom-${idx}`} className="fb-bento-tag">
                        {ci}
                      </span>
                    ))}
                  </>
                ) : (
                  <span className="fb-bento-value">—</span>
                )}
              </div>
            </div>

            {/* Tagesplan - falls vorhanden */}
            {userData.dayHighlights.filter(h => h.text && h.text.trim() !== '').length > 0 && (
              <div className="fb-bento-cell fb-bento-wide">
                <span className="fb-bento-label">Mein Tag</span>
                <div className="fb-bento-timeline">
                  {userData.dayHighlights.filter(h => h.text && h.text.trim() !== '').map((h, idx, arr) => (
                    <React.Fragment key={h.id}>
                      <div className="fb-timeline-item">
                        <span className="fb-timeline-time">{h.time || '—'}</span>
                        <span className="fb-timeline-text">{h.text}</span>
                      </div>
                      {idx < arr.length - 1 && (
                        <div className="fb-timeline-arrow">→</div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Page 9: Übergang 1 */}
      <div className={`fb-page ${currentPage === 9 ? 'active' : ''}`}>
        <div className="fb-page-content">
          <div className="fb-transition-overlay">
            <h1 className="fb-transition-title">
              Fast geschafft, <span className="fb-welcome-name">{userData.name || 'du'}</span>!
            </h1>
          </div>
        </div>
      </div>

      {/* Page 10: Übergang 2 */}
      <div className={`fb-page ${currentPage === 10 ? 'active' : ''}`}>
        <div className="fb-page-content">
          <div className="fb-transition-overlay">
            <p className="fb-transition-text">
              Nur noch eine letzte Frage...
            </p>
          </div>
        </div>
      </div>

      {/* Page 11: Musik-Genres */}
      <div className={`fb-page fb-page-wide ${currentPage === 11 ? 'active' : ''}`}>
        <div className="fb-page-content fb-page-content-wide">
          <h1 className="fb-question-title">Welche Musik hörst du?</h1>

          <div className="fb-input-group">
            <div className="fb-chip-container">
              {GENRES.map((genre) => (
                <React.Fragment key={genre.value}>
                  <div
                    className={`fb-chip ${userData.genres.includes(genre.value) ? 'selected' : ''}`}
                    onClick={() => toggleGenre(genre.value)}
                  >
                    {genre.label}
                  </div>
                  {genre.value === 'edm' && userData.genres.includes('edm') && (
                    EDM_SUBGENRES.map((sub, i) => (
                      <div
                        key={sub.value}
                        className={`fb-chip fb-chip-sub ${userData.edmSubgenres.includes(sub.value) ? 'selected' : ''}`}
                        style={{ animationDelay: `${i * 30}ms` }}
                        onClick={() => toggleEdmSubgenre(sub.value)}
                      >
                        {sub.label}
                      </div>
                    ))
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>

        </div>
      </div>
    </div>
  );
}

export { EDM_SUBGENRES };
export default Freundebuch;
